import { NOTIFICATION_CHANNEL_TYPES } from '@connaro/shared';
import { useState } from 'react';
import { ApiError, api } from '../api/client';
import { useAsync } from '../hooks/useAsync';
import { useT } from '../i18n/I18nProvider';
import { Banner, EmptyState, ErrorState, Loading } from './states';

interface Channel {
  id: string;
  type: string;
  name: string;
  minSeverity: string;
  enabled: boolean;
  secretHint: string;
}

interface Delivery {
  id: string;
  channelId: string;
  status: string;
  detail: string;
  createdAt: string;
}

const SEVERITIES = ['INFO', 'WARNING', 'CRITICAL'] as const;

/**
 * Notification channels: where a tenant's alerts are delivered. Secrets are
 * write-only — the list shows only a masked hint. A "Test" button sends a sample
 * notification, and the delivery log shows what was sent where and whether it worked.
 */
export function NotificationChannelsPanel(): JSX.Element {
  const t = useT();
  const channels = useAsync(() => api.get<Channel[]>('/notification-channels'), []);
  const deliveries = useAsync(() => api.get<Delivery[]>('/notification-channels/deliveries'), []);
  const [error, setError] = useState<string | null>(null);
  const [status, setStatus] = useState<string | null>(null);

  const [type, setType] = useState<string>(NOTIFICATION_CHANNEL_TYPES[0]);
  const [name, setName] = useState('');
  const [secret, setSecret] = useState('');
  const [chatId, setChatId] = useState('');
  const [minSeverity, setMinSeverity] = useState<string>('WARNING');

  const run = async (fn: () => Promise<unknown>): Promise<void> => {
    setError(null);
    try {
      await fn();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : t('errors.unexpected'));
    }
  };

  const add = (e: React.FormEvent): void => {
    e.preventDefault();
    void run(async () => {
      await api.post('/notification-channels', {
        type,
        name,
        secret,
        minSeverity,
        ...(type === 'telegram' && chatId ? { config: { chatId } } : {}),
      });
      setName('');
      setSecret('');
      setChatId('');
      channels.reload();
    });
  };

  const test = (id: string): void =>
    void run(async () => {
      const res = await api.post<{ ok: boolean; detail: string }>(
        `/notification-channels/${id}/test`,
      );
      setStatus(
        res.ok ? t('notifications.testOk') : `${t('notifications.testFailed')}: ${res.detail}`,
      );
      deliveries.reload();
    });

  const remove = (id: string): void =>
    void run(async () => {
      await api.del(`/notification-channels/${id}`);
      channels.reload();
    });

  return (
    <div className="card">
      <h2>{t('notifications.title')}</h2>
      <p>{t('notifications.intro')}</p>
      {error ? <Banner variant="error">{error}</Banner> : null}
      {status ? <Banner variant="info">{status}</Banner> : null}

      <form className="form form--inline" onSubmit={add}>
        <label className="field">
          <span>{t('notifications.type')}</span>
          <select value={type} onChange={(e) => setType(e.target.value)}>
            {NOTIFICATION_CHANNEL_TYPES.map((ty) => (
              <option key={ty} value={ty}>
                {ty}
              </option>
            ))}
          </select>
        </label>
        <label className="field">
          <span>{t('notifications.name')}</span>
          <input value={name} onChange={(e) => setName(e.target.value)} required />
        </label>
        <label className="field">
          <span>{t('notifications.secret')}</span>
          <input
            type="password"
            placeholder={t('notifications.secretHint')}
            value={secret}
            onChange={(e) => setSecret(e.target.value)}
            required
          />
        </label>
        {type === 'telegram' ? (
          <label className="field">
            <span>{t('notifications.chatId')}</span>
            <input value={chatId} onChange={(e) => setChatId(e.target.value)} />
          </label>
        ) : null}
        <label className="field">
          <span>{t('notifications.minSeverity')}</span>
          <select value={minSeverity} onChange={(e) => setMinSeverity(e.target.value)}>
            {SEVERITIES.map((s) => (
              <option key={s} value={s}>
                {s}
              </option>
            ))}
          </select>
        </label>
        <button type="submit" className="btn">
          {t('notifications.add')}
        </button>
      </form>

      {channels.loading ? (
        <Loading />
      ) : channels.error ? (
        <ErrorState message={channels.error.message} onRetry={channels.reload} />
      ) : !channels.data || channels.data.length === 0 ? (
        <EmptyState hint={t('notifications.none')} />
      ) : (
        <div className="table-wrap">
          <table>
            <thead>
              <tr>
                <th>{t('notifications.type')}</th>
                <th>{t('notifications.name')}</th>
                <th>{t('notifications.minSeverity')}</th>
                <th>{t('notifications.secret')}</th>
                <th aria-label="actions" />
              </tr>
            </thead>
            <tbody>
              {channels.data.map((c) => (
                <tr key={c.id}>
                  <td>{c.type}</td>
                  <td>{c.name}</td>
                  <td>{c.minSeverity}</td>
                  <td>{c.secretHint}</td>
                  <td className="row-actions">
                    <button type="button" className="btn btn--ghost" onClick={() => test(c.id)}>
                      {t('notifications.test')}
                    </button>
                    <button
                      type="button"
                      className="btn btn--ghost btn--danger"
                      onClick={() => remove(c.id)}
                    >
                      {t('notifications.delete')}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <h3>{t('notifications.deliveries')}</h3>
      {!deliveries.data || deliveries.data.length === 0 ? (
        <EmptyState hint={t('notifications.noDeliveries')} />
      ) : (
        <div className="table-wrap">
          <table>
            <thead>
              <tr>
                <th>{t('notifications.when')}</th>
                <th>{t('notifications.status')}</th>
                <th>{t('notifications.detail')}</th>
              </tr>
            </thead>
            <tbody>
              {deliveries.data.slice(0, 20).map((d) => (
                <tr key={d.id}>
                  <td>{new Date(d.createdAt).toLocaleString()}</td>
                  <td>
                    <span className={`chip chip--${d.status === 'SENT' ? 'success' : 'critical'}`}>
                      {d.status}
                    </span>
                  </td>
                  <td>{d.detail}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
